import { useEffect, useState } from "react";

import { Award, CreditCard } from "lucide-react";

import { PaywallDialog } from "src/components/PaywallDialog/PaywallDialog";
import { Button } from "src/components/ui/button";
import { Progress } from "src/components/ui/progress";
import { useSidebar } from "src/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useAuth } from "src/lib/auth";
import { DEFAULT_TRIAL_DAYS, storeIntegrationTypes } from "src/lib/constants";
import { useApiQuery } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { PAYMENT_METHOD_QUERY } from "src/lib/queries/billing";
import { shouldShowStartTrialCard } from "src/lib/stores";
import { cn, pluralize } from "src/lib/utils";

const APP_NAME = process.env.APP_NAME || "the app";

export function TrialCountdown({ className }) {
  const { state, isMobile } = useSidebar();
  const { currentUser } = useAuth();
  const { integrations, loading } = useIntegration(
    storeIntegrationTypes.shopify,
    {
      queryOnRender: false,
    }
  );
  const [hasCheckedPaymentMethod, setHasCheckedPaymentMethod] = useState(false);
  const { data: paymentMethodData, queryApi: queryPaymentMethod } = useApiQuery(
    {
      query: PAYMENT_METHOD_QUERY,
    }
  );

  const daysLeft = currentUser?.trialDaysLeft ?? 0;
  const trialDays = currentUser?.trialDays ?? DEFAULT_TRIAL_DAYS;
  const progress = (daysLeft / trialDays) * 100;
  const hasPaymentMethod = !!paymentMethodData?.paymentMethod?.last4;
  const showActiveTrialCard = daysLeft > 0;
  const showStartTrialCard = shouldShowStartTrialCard(daysLeft, integrations);
  const showExpiredPaymentMethodCard =
    daysLeft === 0 && hasCheckedPaymentMethod && !hasPaymentMethod;
  const isCollapsedDesktop = state === "collapsed" && !isMobile;

  useEffect(() => {
    let active = true;

    if (daysLeft !== 0) {
      setHasCheckedPaymentMethod(false);
      return;
    }

    queryPaymentMethod().finally(() => {
      if (active) {
        setHasCheckedPaymentMethod(true);
      }
    });

    return () => {
      active = false;
    };
  }, [daysLeft, queryPaymentMethod]);

  if (
    loading ||
    (daysLeft === 0 && !hasCheckedPaymentMethod) ||
    (!showActiveTrialCard &&
      !showStartTrialCard &&
      !showExpiredPaymentMethodCard)
  ) {
    return null;
  }

  if (isCollapsedDesktop) {
    if (showExpiredPaymentMethodCard) {
      return (
        <div className={cn("mx-2", className)}>
          <PaywallDialog
            source="trialEnded"
            onClick={() => {
              queryPaymentMethod();
            }}
          >
            <Button
              size="icon"
              title={`Trial ended. Add a payment method to continue using ${APP_NAME}.`}
              className="relative size-8 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/85 to-primary shadow-md shadow-primary/20 hover:from-primary hover:to-primary/90"
            >
              <Award className="size-4" />
            </Button>
          </PaywallDialog>
        </div>
      );
    }

    if (showStartTrialCard) {
      return (
        <div className={cn("mx-2", className)}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="size-8 rounded-lg"
                onClick={() => {
                  window.open(process.env.SHOPIFY_LISTING_URL, "_blank");
                }}
              >
                <Award className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              Start {trialDays} day free trial
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return (
      <div className={cn("mx-2", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="bg-background flex size-8 items-center justify-center rounded-lg border border-border">
              <Award className="text-primary size-4" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            Free trial: {daysLeft} {pluralize("day", daysLeft)} left
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-2 flex flex-col rounded-lg bg-background p-4",
        showExpiredPaymentMethodCard
          ? "relative gap-3 overflow-hidden border border-border/70 shadow-lg"
          : "gap-2",
        className
      )}
    >
      {showExpiredPaymentMethodCard && (
        <>
          <div className="absolute inset-0 bg-gradient-to-br from-primary/12 via-background to-background" />
          <div className="absolute -top-10 right-[-1rem] h-28 w-28 rounded-full bg-primary/14 blur-2xl" />
          <div className="absolute -bottom-10 left-[-1rem] h-24 w-24 rounded-full bg-primary/10 blur-2xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </>
      )}

      <div
        className={cn(
          "relative flex w-full items-center justify-between",
          showExpiredPaymentMethodCard && "items-start justify-start"
        )}
      >
        <span
          className={cn(
            "text-sm flex items-center gap-1",
            showExpiredPaymentMethodCard && "gap-2.5"
          )}
        >
          {showExpiredPaymentMethodCard ? (
            <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-xl border border-primary/20">
              <CreditCard className="size-4" />
            </span>
          ) : (
            <Award className="size-4" />
          )}
          <span
            className={cn(
              showExpiredPaymentMethodCard && "flex flex-col gap-0.5"
            )}
          >
            {showExpiredPaymentMethodCard && (
              <span className="text-muted-foreground font-medium text-xs tracking-widest uppercase">
                Billing Required
              </span>
            )}
            <span
              className={cn(showExpiredPaymentMethodCard && " font-semibold")}
            >
              {showExpiredPaymentMethodCard ? "Trial Ended" : "Free Trial"}
            </span>
          </span>
        </span>

        {showActiveTrialCard && (
          <span className="text-muted-foreground text-sm">
            {daysLeft} {pluralize("day", daysLeft)} left
          </span>
        )}
        {showStartTrialCard && (
          <span className="text-muted-foreground text-sm">Start today</span>
        )}
      </div>

      {showExpiredPaymentMethodCard && (
        <p className="text-muted-foreground relative text-sm">
          Your free trial has ended. To continue using {APP_NAME}, please add a
          payment method.
        </p>
      )}

      {showActiveTrialCard && <Progress value={progress} />}

      {showStartTrialCard && (
        <Button
          className="mt-2"
          onClick={() => {
            window.open(process.env.SHOPIFY_LISTING_URL, "_blank");
          }}
        >
          Start {trialDays} Days Free Trial
        </Button>
      )}

      {showExpiredPaymentMethodCard && (
        <PaywallDialog
          source="trialEnded"
          onClick={() => {
            queryPaymentMethod();
          }}
        >
          <Button className="relative mt-1 rounded-xl shadow-lg shadow-primary/20">
            Add Payment Method
          </Button>
        </PaywallDialog>
      )}
    </div>
  );
}
